import type { IconName } from "@helmr/ui";
import { LEVERS, type LeverId } from "@helmr/contracts";
import type { Context } from "./permissions";
import { settingsAreaAllows } from "../features/settings/sections";

/** `ctx.has`: does the caller hold this lever (D57)? */
export type Has = Context["has"];

const any = (has: Has, levers: readonly LeverId[]) => levers.some((l) => has(l));
/** The eight report levers: the Reports area shows if any one report may be opened. */
export const REPORT_LEVERS: readonly LeverId[] = LEVERS.filter((l) => l.group === "reports").map((l) => l.id);
/** The attendance page has two tabs: the check-in log and at-risk members, each on its own lever. */
export const ATTENDANCE_LEVERS = ["members.checkins", "members.at_risk"] as const satisfies readonly LeverId[];

export type AreaKey =
  | "home" | "inbox" | "more" | "members" | "attendance" | "billing" | "dues" | "leads" | "followups" | "classes" | "staff" | "payroll" | "expenses" | "reports" | "settings" | "kiosk";

export interface Area {
  key: AreaKey;
  label: string;
  path: string;
  icon: IconName;
  description: string;
  /** In the sidebar? `followups` and `kiosk` are routes only. */
  sidebar: boolean;
  /**
   * Visible for this role? The same function guards the page itself (`areaAllows`), so the nav never offers a
   * page that then says "no access", and never hides one the role may open. Hiding is cosmetic; the server
   * enforces the lever on every route.
   */
  allow: (has: Has) => boolean;
  /** Pinned to the bottom of the sidebar. */
  end?: boolean;
  /** Draws the separator line above this item. */
  separatorBefore?: boolean;
}

/** Order is the reference's sidebar order. Each `allow` names the lever(s) the area's page and its first API read need. */
export const AREAS: readonly Area[] = [
  { key: "home", label: "Home", path: "/home", icon: "home", description: "Your day at a glance.", sidebar: true, allow: () => true },
  { key: "inbox", label: "Inbox", path: "/inbox", icon: "chat", description: "WhatsApp chats with leads, members and staff.", sidebar: true, allow: (h) => h("inbox.view") },
  { key: "members", label: "Members", path: "/members", icon: "users", description: "Every member, their plan, renewals and history.", sidebar: true, allow: (h) => h("members.view") },
  { key: "billing", label: "Billing", path: "/billing", icon: "bill", description: "Bills, GST invoices, payment links and refunds.", sidebar: true, allow: (h) => h("billing.bills_view") },
  { key: "dues", label: "Dues", path: "/dues", icon: "wallet", description: "Who owes what, and the reminders going out.", sidebar: true, allow: (h) => h("billing.bills_view") },
  { key: "leads", label: "Leads", path: "/leads", icon: "inbox", description: "Enquiries, trials and follow-ups in one pipeline.", sidebar: true, allow: (h) => h("leads.view") },
  { key: "classes", label: "Classes", path: "/classes", icon: "dumbbell", description: "Class schedule, bookings and capacity.", sidebar: true, allow: (h) => h("classes.view") || h("pt.view") },
  { key: "attendance", label: "Attendance", path: "/attendance", icon: "finger", description: "Check-ins from the biometric reader and QR.", sidebar: true, allow: (h) => any(h, ATTENDANCE_LEVERS) },
  { key: "staff", label: "Staff", path: "/staff", icon: "badge", description: "Coaches, shifts and punches.", sidebar: true, separatorBefore: true, allow: (h) => h("staff.view") },
  { key: "payroll", label: "Payroll", path: "/payroll", icon: "cash", description: "Salaries, PT commissions and payouts.", sidebar: true, allow: (h) => h("payroll.view") },
  { key: "expenses", label: "Expenses", path: "/expenses", icon: "receipt", description: "Rent, utilities and everything else you spend.", sidebar: true, allow: (h) => h("expenses.view") },
  { key: "reports", label: "Reports", path: "/reports", icon: "chart", description: "Collections, GST and retention reports.", sidebar: true, allow: (h) => any(h, REPORT_LEVERS) },
  { key: "settings", label: "Settings", path: "/settings", icon: "gear", description: "Plans, taxes, branches, users and integrations.", sidebar: true, end: true, allow: (h) => settingsAreaAllows(h) },
  { key: "followups", label: "Follow-ups", path: "/followups", icon: "clock", description: "Every follow-up, with filters.", sidebar: false, allow: (h) => h("leads.view") || h("members.view") },
  { key: "more", label: "More", path: "/more", icon: "grid", description: "Everything else, and sign out.", sidebar: false, allow: () => false },
  { key: "kiosk", label: "Check-in kiosk", path: "/kiosk", icon: "finger", description: "Use this device as a check-in kiosk.", sidebar: false, allow: (h) => h("devices.kiosks_add") },
];

export const areaByKey = (key: AreaKey): Area => AREAS.find((a) => a.key === key)!;

/** The page guard for an area: exactly the nav's `allow`, so the two can never disagree. */
export const areaAllows = (key: AreaKey, has: Has): boolean => areaByKey(key).allow(has);
